<template>
  <div class="settings-box">
    <div class="row">
      <div class="col">
        <h4 class="heading">
          {{ $t('APP_TEXT_SETTINGS_HEADING') }}
        </h4>
      </div>
    </div>
    <div class="row">
      <div class="col-6">
        <form-input
          v-if="noVideoDevice"
          type="text"
          :placeholder="$t('APP_TEXT_NO_CAMERA_FOUND')"
          :label="{text: $t('APP_TEXT_CAMERA'), icon: 'SvgCameraFill'}"
          :disabled="noVideoDevice"
        />
        <div
          v-else
          class="form-group"
        >
          <label>
            <svg-icon
              icon="SvgCameraFill"
              class="label-left-icon"
            />
            {{ $t('APP_TEXT_CAMERA') }}
          </label>
          <div class="position-relative">
            <v-select
              v-if="videoInputDevices && videoInputDevices.length > 0"
              v-model="selectedVideoInput"
              :placeholder="$t('APP_TEXT_CAMERA')"
              :options="videoInputDevices"
              label="label"
              :clearable="false"
              :push-tags="false"
              :taggable="false"
              class="form-control"
              :disabled="noVideoDevice"
              @input="onChangeVideoInput"
            />
          </div>
        </div>
        <div class="pic-in-preview">
          <video
            v-show="!noVideoDevice && videoStream && selectedVideoInput && selectedVideoInput.deviceId && !error"
            ref="settingsVideoElem"
            class="pic-in-content"
            autoplay
            muted
          />
          <div
            v-show="noVideoDevice || !selectedVideoInput || !selectedVideoInput.deviceId"
            class="pic-in-content"
          >
            {{ $t('APP_TEXT_NO_VIDEO_DEVICE_SELECTED') }}
            <svg-icon icon="SvgVideoStopped" />
          </div>
        </div>
      </div>
      <div class="col-6">
        <form-input
          v-if="noAudioInput"
          type="text"
          :placeholder="$t('APP_TEXT_NO_MICROPHONE_FOUND')"
          :label="{text: $t('APP_TEXT_MICROPHONE'), icon: 'SvgMic'}"
          :disabled="noAudioInput"
        />
        <div
          v-else
          class="form-group"
        >
          <label>
            <svg-icon
              icon="SvgMic"
              class="label-left-icon"
            />
            {{ $t('APP_TEXT_MICROPHONE') }}
          </label>
          <div class="position-relative">
            <v-select
              v-if="audioInputDevices && audioInputDevices.length > 0"
              v-model="selectedAudioInput"
              :placeholder="$t('APP_TEXT_MICROPHONE')"
              :options="audioInputDevices"
              label="label"
              :clearable="false"
              :push-tags="false"
              :taggable="false"
              :disabled="noAudioInput"
              class="form-control"
              @input="onChangeAudioInput"
            />
          </div>
        </div>
        <div class="progress mb-3">
          <div
            class="progress-bar bg-success"
            role="progressbar"
            :style="{width: `${audioInputLevel}%`}"
            :aria-valuenow="audioInputLevel"
            aria-valuemin="0"
            aria-valuemax="100"
          />
        </div>
        <template v-if="showAudioOutput">
          <form-input
            v-if="noAudioOutput"
            type="text"
            :placeholder="$t('APP_TEXT_NO_SPEAKER_FOUND')"
            :label="{text: $t('APP_TEXT_SPEAKER'), icon: 'SvgSpeakerOn'}"
            :disabled="noAudioOutput"
          />
          <div
            v-else
            class="form-group"
          >
            <label>
              <svg-icon
                icon="SvgSpeakerOn"
                class="label-left-icon"
              />
              {{ $t('APP_TEXT_SPEAKER') }}
            </label>
            <div class="position-relative">
              <v-select
                v-if="audioOutputDevices && audioOutputDevices.length > 0"
                v-model="selectedAudioOutput"
                :placeholder="$t('APP_TEXT_SPEAKER')"
                :options="audioOutputDevices"
                label="label"
                :clearable="false"
                :push-tags="false"
                :taggable="false"
                :disabled="noAudioOutput"
                class="form-control"
                @input="onChangeAudioOutput"
              />
            </div>
          </div>
          <div class="form-group btn-play">
            <form-button
              v-if="testAudio && !noAudioOutput"
              :text="testAudio.paused ? $t('APP_TEXT_PLAY_TEST_SOUND') : $t('APP_TEXT_PAUSE_TEST_SOUND')"
              :icon="testAudio.paused ? 'Svgplay' : 'SvgPause'"
              link
              @onClick="playAudio"
            />
          </div>
        </template>
        <div
          v-if="showDeviceError"
          class="row m-0"
        >
          <div class="col message-box">
            {{ $t('APP_TEXT_DEVICE_MISSING') }}
          </div>
        </div>
      </div>
    </div>
    <div class="row mt-3 mb-2">
      <div class="col-4 remember-me">
        <FormCheckbox
          v-model="remember"
          data-qa="remember_me_checkbox"
          :disabled="showDeviceError"
        />
        <label class="text-primary">&nbsp;{{ $t('APP_TEXT_REMEMBER_SETTINGS') }}</label>
      </div>
      <div class="col text-right">
        <form-button
          v-if="showDeviceError"
          type="primary"
          btn-type="button"
          :text="$t('APP_TEXT_RELOAD')"
          @onClick="reloadSettings"
        />
        <form-button
          v-else
          type="primary"
          btn-type="button"
          :text="$t('APP_TEXT_RESET_SETTINGS')"
          @onClick="resetToDefault"
        />
        <div class="form-group btn-connect ml-3">
          <form-button
            type="success"
            icon="SvgCall"
            rounded
            :disabled="showDeviceError"
            @onClick="startMeeting"
          />
          <form-button
            class="btn-secondary mb-0"
            btn-type="submit"
            :text="$t('APP_TEXT_CONNECT')"
            :disabled="showDeviceError"
            @onClick="startMeeting"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SettingsWidget',
  props: {
    soundFile: {
      type: String,
      default: ''
    },
    storedConfig: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      videoInputDevices: [],
      audioInputDevices: [],
      audioOutputDevices: [],
      otherDevices: [],
      audioInput: {},
      audioInputLevel: 0,
      remember: false,
      error: false,
      videoStream: null,
      selectedVideoInput: null,
      selectedAudioInput: null,
      selectedAudioOutput: null,
      testAudio: null
    };
  },
  computed: {
    noVideoDevice () {
      return !this.videoInputDevices || this.videoInputDevices.length <= 0;
    },
    noAudioInput () {
      return !this.audioInputDevices || this.audioInputDevices.length <= 0;
    },
    showAudioOutput () {
      return !(this.$_UTILS.isFirefox() || this.$_UTILS.isSafari());
    },
    noAudioOutput () {
      if (!this.showAudioOutput) {
        return false;
      }
      return !this.audioOutputDevices || this.audioOutputDevices.length <= 0;
    },
    showDeviceError () {
      return (!this.videoInputDevices || this.videoInputDevices.length <= 1) && this.noAudioInput;
    }
  },
  watch: {
    remember (newValue, oldValue) {
      this.onSettingsChange();
    }
  },
  async mounted () {
    await this.refreshDeviceList();
  },
  beforeDestroy () {
    this.closeStream();
    this.pauseAudio();
  },
  methods: {
    async getDataFromStore () {
      if (this.storedConfig) {
        if (this.storedConfig.audioInput) {
          this.resetAudioInput(this.storedConfig.audioInput);
        } else {
          this.resetAudioInput();
        }
        if (this.storedConfig.audioOutput) {
          this.resetAudioOutput(this.storedConfig.audioOutput);
        } else {
          this.resetAudioOutput();
        }
        if (this.storedConfig.videoInput) {
          this.resetVideoInput(this.storedConfig.videoInput);
        } else {
          this.resetVideoInput();
        }
        this.remember = !!this.storedConfig.remember;
      } else {
        this.resetToDefault();
      }
    },
    async refreshDeviceList () {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
        stream.getTracks().forEach((track) => {
          track.stop();
        });
        const deviceList = await navigator.mediaDevices.enumerateDevices();
        await this.gotDevices(deviceList);
        await this.getDataFromStore();
        this.playVideo();
        this.createAudio();
      } catch (error) {
        this.error = false;
      }
    },
    /**
     * On device list obtained
     * @param {array} deviceList List of all Media Devices
     */
    async gotDevices (deviceList) {
      this.audioInputDevices = [];
      this.audioOutputDevices = [];
      this.videoInputDevices = [];
      deviceList.forEach((device) => {
        switch (device.kind) {
          case 'audioinput':
            this.audioInputDevices.push({
              deviceId: device.deviceId,
              groupId: device.groupId,
              kind: device.kind,
              label: device.label || `microphone ${this.audioInputDevices.length + 1}`
            });
            break;
          case 'audiooutput':
            this.audioOutputDevices.push({
              deviceId: device.deviceId,
              groupId: device.groupId,
              kind: device.kind,
              label: device.label || `speaker ${this.audioOutputDevices.length + 1}`
            });
            break;
          case 'videoinput':
            this.videoInputDevices.push({
              deviceId: device.deviceId,
              groupId: device.groupId,
              kind: device.kind,
              label: device.label || `camera ${this.videoInputDevices.length + 1}`
            });
            break;
          default:
            this.otherMediaDevices.push({
              deviceId: device.deviceId,
              groupId: device.groupId,
              kind: device.kind,
              label: device.label || `device ${this.otherMediaDevices.length + 1}`
            });
            break;
        }
      });
      this.audioInputDevices = this.formatDeviceList(this.audioInputDevices);
      this.audioOutputDevices = this.formatDeviceList(this.audioOutputDevices);
      this.videoInputDevices = this.videoInputDevices.concat({ label: 'None', deviceId: false });
      return true;
    },
    /**
     * Format device list based on priority
     * deviceId [default > communications > others(any Id))]
     * @param {array} devices Media Device list
     */
    formatDeviceList (devices) {
      const deviceList = {};
      devices.forEach((device) => {
        if (!device.groupId) {
          deviceList[device.deviceId] = device;
        } else if (!deviceList[device.groupId]) {
          deviceList[device.groupId] = device;
        } else {
          if (device.deviceId === 'default') {
            deviceList[device.groupId] = device;
          }
          if (device.deviceId === 'communications' && deviceList[device.groupId].deviceId !== 'default') {
            deviceList[device.groupId] = device;
          }
          if (device.deviceId && deviceList[device.groupId].deviceId !== 'default' && deviceList[device.groupId].deviceId !== 'communications') {
            deviceList[device.groupId] = device;
          }
        }
      });
      return Object.values(deviceList);
    },
    resetToDefault () {
      this.resetAudioInput();
      this.resetAudioOutput();
      this.resetVideoInput();
      this.remember = false;
      this.onSettingsChange();
      this.playVideo();
    },
    onSettingsChange (gotoMeeting = false) {
      this.$emit(gotoMeeting ? 'onMeeting' : 'onSettingsChange', {
        audioInput: this.selectedAudioInput,
        audioOutput: this.selectedAudioOutput,
        videoInput: this.selectedVideoInput,
        remember: this.remember
      });
    },
    resetAudioInput (id = 'default') {
      this.selectedAudioInput = this.audioInputDevices.find((dev) => dev.deviceId === id) || this.audioInputDevices[0];
    },
    resetAudioOutput (id = 'default') {
      this.selectedAudioOutput = this.audioOutputDevices.find((dev) => dev.deviceId === id) || this.audioOutputDevices[0];
    },
    resetVideoInput (id = null) {
      this.selectedVideoInput = this.videoInputDevices.find((dev) => dev.deviceId === id) || this.videoInputDevices[0];
    },
    startMeeting () {
      this.onSettingsChange(true);
    },
    async playVideo () {
      const constraints = {
        audio: true,
        video: {
          width: {
            min: 320,
            ideal: 1280,
            max: 1280
          },
          height: {
            min: 180,
            ideal: 720,
            max: 720
          },
          aspectRatio: 1.7777777778
        }
      };
      if (this.selectedAudioInput) {
        constraints.audio = {
          deviceId: { exact: this.selectedAudioInput.deviceId }
        };
      }
      if (!this.selectedVideoInput || !this.selectedVideoInput.deviceId) {
        constraints.video = false;
      }
      if (this.selectedVideoInput && this.selectedVideoInput.deviceId) {
        constraints.video.deviceId = { exact: this.selectedVideoInput.deviceId };
      }

      try {
        await this.closeStream();
        this.videoStream = await navigator.mediaDevices.getUserMedia(constraints);
        this.error = false;
        if (this.$refs.settingsVideoElem) {
          this.$refs.settingsVideoElem.srcObject = this.videoStream;
        }
        this.audioInputMonitor();
      } catch (ex) {
        this.handleVideoError(ex);
      }
    },
    async closeStream () {
      if (this.videoStream) {
        try {
          const tracks = this.videoStream.getTracks();
          await tracks.forEach(async track => {
            await track.stop();
          });
          this.videoStream = null;
        } catch (error) {
          this.videoStream = null;
        }
        if (this.$refs.settingsVideoElem) {
          this.$refs.settingsVideoElem.srcObject = this.videoStream;
        }
      }
    },
    audioInputMonitor () {
      if (this.videoStream) {
        this.audioInput.audioContext = this.audioInput.audioContext ? this.audioInput.audioContext : new AudioContext();
        this.audioInput.analyser = this.audioInput.analyser ? this.audioInput.analyser : this.audioInput.audioContext.createAnalyser();
        this.audioInput.microphone = this.audioInput.audioContext.createMediaStreamSource(this.videoStream);
        this.audioInput.scriptProcessor = this.audioInput.audioContext.createScriptProcessor(2048, 1, 1);

        this.audioInput.analyser.smoothingTimeConstant = 0.3;
        this.audioInput.analyser.fftSize = 1024;

        this.audioInput.microphone.connect(this.audioInput.analyser);
        this.audioInput.analyser.connect(this.audioInput.scriptProcessor);
        this.audioInput.scriptProcessor.connect(this.audioInput.audioContext.destination);

        const self = this;
        this.audioInput.scriptProcessor.onaudioprocess = () => {
          var array = new Uint8Array(self.audioInput.analyser.frequencyBinCount);
          self.audioInput.analyser.getByteFrequencyData(array);
          var values = 0;

          var length = array.length;
          for (var i = 0; i < length; i++) {
            values += array[i];
          }

          var level = values / length;
          self.audioInputLevel = ((level / 160) * 100) || 0;
        };
      }
    },
    handleVideoError (error) {
      // eslint-disable-next-line no-console
      console.log('navigator.getUserMedia error: ', error);
      this.error = true;
    },
    async onChangeAudioInput () {
      this.playVideo();
      this.onSettingsChange();
    },
    onChangeAudioOutput () {
      this.onSettingsChange();
    },
    async onChangeVideoInput () {
      this.playVideo();
      this.onSettingsChange();
    },
    async createAudio () {
      if (this.soundFile && !this.testAudio) {
        this.testAudio = new Audio(this.soundFile);
        this.testAudio.loop = true;
        await this.testAudio.load();
      }
    },
    async playAudio () {
      if (this.selectedAudioOutput && this.selectedAudioOutput.deviceId) {
        await this.createAudio();
        if (this.testAudio && this.testAudio.paused) {
          this.testAudio.currentTime = 0;
          await this.testAudio.setSinkId(this.selectedAudioOutput.deviceId);
          this.testAudio.play();
        } else {
          this.pauseAudio();
        }
      }
    },
    pauseAudio () {
      if (this.testAudio) {
        this.testAudio.pause();
      }
    },
    reloadSettings () {
      this.refreshDeviceList();
    }
  }
};
</script>

<style lang="scss" scoped>

</style>
